import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { events } from "@/mocks/events";
import { categories } from "@/mocks/categories";
import EventCard from "@/components/feature/EventCard";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import usePageMeta from "@/hooks/usePageMeta";
import EventsToolbar from "@/pages/events/components/EventsToolbar";

export default function Events() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get("cat") ?? "all";
  const initialQ = searchParams.get("q") ?? "";

  const [activeCat, setActiveCat] = useState(initialCat);
  const [keyword, setKeyword] = useState(initialQ);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return events
      .filter((e) => (activeCat === "all" ? true : e.categorySlug === activeCat))
      .filter((e) => {
        if (!kw) return true;
        return (
          e.title.toLowerCase().includes(kw) ||
          e.summary.toLowerCase().includes(kw) ||
          e.tags.some((t) => t.toLowerCase().includes(kw)) ||
          e.category.toLowerCase().includes(kw)
        );
      })
      .sort((a, b) => b.heat - a.heat);
  }, [activeCat, keyword]);

  const activeCatName =
    activeCat === "all"
      ? "全部事件"
      : categories.find((c) => c.slug === activeCat)?.name ?? "全部事件";

  const handleCatChange = (slug: string) => {
    setActiveCat(slug);
    const next = new URLSearchParams(searchParams);
    if (slug === "all") next.delete("cat");
    else next.set("cat", slug);
    setSearchParams(next, { replace: true });
  };

  const handleKeywordChange = (value: string) => {
    setKeyword(value);
    const next = new URLSearchParams(searchParams);
    if (value.trim()) next.set("q", value.trim());
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const jsonLd = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "热门吃瓜事件库",
        description: "瓜线已梳理的热门吃瓜事件合集，支持按主题分类与关键词查找。",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "事件库", item: "/events" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        numberOfItems: filtered.length,
        itemListElement: filtered.map((e, idx) => ({
          "@type": "ListItem",
          position: idx + 1,
          name: e.title,
          url: `/event/${e.id}`,
        })),
      },
    ],
    [filtered]
  );

  usePageMeta({
    title: "热门吃瓜事件库｜时间线合集与全程复盘 - 瓜线",
    description:
      "瓜线事件库收录全部热门吃瓜事件，可按娱乐圈、直播电商、综艺、音乐演出等主题筛选，也能搜索关键词，快速找到你想要的时间线与前因后果复盘。",
    keywords: "热门吃瓜事件库,吃瓜时间线合集,热门吃瓜分类,热门吃瓜复盘,吃瓜事件整理",
    path: "/events",
    jsonLd,
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100 px-4 md:px-8 py-10 md:py-14">
        <div className="w-full">
          <Breadcrumbs items={[{ name: "首页", path: "/" }, { name: "事件库" }]} />
          <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <h1 className="font-heading text-2xl md:text-4xl font-extrabold leading-tight text-foreground-950">
                热门吃瓜事件库
              </h1>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                这里收录了瓜线已梳理的全部事件档案。按主题分类筛选，或者直接搜索关键词，
                找到事件后即可查看完整的时间线、相关人物与前因后果复盘。
              </p>
            </div>
            <div className="flex gap-6">
              <div>
                <div className="font-heading text-2xl font-bold text-primary-600">
                  {events.length}
                </div>
                <div className="text-xs text-foreground-500">已梳理事件</div>
              </div>
              <div className="w-px bg-background-300" />
              <div>
                <div className="font-heading text-2xl font-bold text-secondary-600">
                  {categories.length}
                </div>
                <div className="text-xs text-foreground-500">主题分类</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-50">
        <div className="w-full">
          <EventsToolbar
            activeCat={activeCat}
            onCatChange={handleCatChange}
            keyword={keyword}
            onKeywordChange={handleKeywordChange}
            resultCount={filtered.length}
          />

          <h2 className="mt-8 font-heading text-lg font-bold text-foreground-950">
            {activeCatName}
            {keyword.trim() && (
              <span className="ml-2 text-sm font-normal text-foreground-500">
                匹配「{keyword.trim()}」
              </span>
            )}
          </h2>

          {filtered.length > 0 ? (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((event) => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-400">
                <i className="ri-search-eye-line" />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">
                没有找到匹配的事件
              </h3>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                换个关键词试试，或者切回「全部」分类看看其它正在升温的故事线。
              </p>
              <button
                type="button"
                onClick={() => {
                  handleCatChange("all");
                  handleKeywordChange("");
                }}
                className="mt-5 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600 cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-refresh-line" />
                </span>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}